import { useCallback, useEffect, useState } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Group from 'components/utils/Group'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { consentMessages } from '../../../lib/consent/messages'
import { OPEN_CONSENT_SETTINGS_EVENT } from '../../consent/openSettings'
import { reloadPage } from '../../consent/reloadPage'
import { clearUngrantedPurposeIdentifiers } from '../../consent/clearWithdrawnIdentifiers'
import { continueWithoutOptionalCookies, readConsentState, writeConsentState } from '../../consent/deviceState'
import { useConsentBackend } from '../../consent/useConsentBackend'
import type { ConsentBackendError } from '../../consent/useConsentBackend'
import type { ConsentDecision } from '../../../lib/consent/backendContract'
import { CONSENT_PURPOSES } from '../../consent/types'
import type { ConsentPurpose, ConsentState } from '../../consent/types'
import ConsentPurposeRow from './ConsentPurposeRow'

type ConsentView = 'first-layer' | 'configure' | 'confirmation'

const APPLY_RELOAD_DELAY_MS = 250

const OPTIONAL_PURPOSE_COPY = [
  {
    purpose: 'analytics' as const,
    title: consentMessages.analytics,
    description: consentMessages.analyticsDescription,
  },
  {
    purpose: 'personalisation' as const,
    title: consentMessages.personalisation,
    description: consentMessages.personalisationDescription,
  },
  {
    purpose: 'experimentation' as const,
    title: consentMessages.experimentation,
    description: consentMessages.experimentationDescription,
  },
  {
    purpose: 'advertising' as const,
    title: consentMessages.advertising,
    description: consentMessages.advertisingDescription,
  },
]

function getErrorMessage(error: ConsentBackendError | undefined, backendEnabled: boolean) {
  if (error === 'conflict') return consentMessages.saveConflict
  if (error === 'unconfirmed') return consentMessages.saveUnconfirmed
  return backendEnabled ? consentMessages.backendError : consentMessages.saveError
}

function ConsentBanner() {
  const intl = useIntl()
  const currentConsent = readConsentState()
  const [isOpen, setIsOpen] = useState(!currentConsent)
  const [view, setView] = useState<ConsentView>(currentConsent ? 'configure' : 'first-layer')
  const [openedFromSettings, setOpenedFromSettings] = useState(!!currentConsent)
  const [purposes, setPurposes] = useState<Array<ConsentPurpose>>(currentConsent?.purposes ?? [])
  const [isApplying, setIsApplying] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const onBackendLoaded = useCallback((saved?: ConsentState, pending?: ConsentDecision) => {
    setPurposes(saved?.purposes ?? pending?.consents.filter(consent => consent.granted).map(consent => consent.purpose_key) ?? [])
    setOpenedFromSettings(!!saved)
    setView(saved || pending ? 'configure' : 'first-layer')
    setIsOpen(!saved)
  }, [])
  const onBackendConflict = useCallback((nextPurposes: Array<ConsentPurpose>) => {
    setPurposes(nextPurposes)
    setView('configure')
    setIsOpen(true)
  }, [])
  const backend = useConsentBackend(intl.locale, onBackendLoaded, onBackendConflict)
  const { save: saveBackendChoice } = backend
  const isBusy = isApplying || backend.busy

  useEffect(() => {
    const openSettings = () => {
      const current = readConsentState()
      setPurposes(current?.purposes ?? [])
      setOpenedFromSettings(true)
      setView('configure')
      setIsApplying(false)
      setSaveFailed(false)
      setIsOpen(true)
    }
    window.addEventListener(OPEN_CONSENT_SETTINGS_EVENT, openSettings)
    return () => window.removeEventListener(OPEN_CONSENT_SETTINGS_EVENT, openSettings)
  }, [])

  useEffect(() => {
    if (!isApplying) return
    const timeout = window.setTimeout(() => {
      try {
        clearUngrantedPurposeIdentifiers(readConsentState()?.purposes ?? [])
      } finally {
        // Stopping existing integrations must not depend on storage availability.
        reloadPage()
      }
    }, APPLY_RELOAD_DELAY_MS)
    return () => window.clearTimeout(timeout)
  }, [isApplying])

  const privacyURL = config.contentLinks.privacyPolicy.href || config.contentLinks.privacyPolicy.to!

  const save = useCallback((nextPurposes: Array<ConsentPurpose>) => {
    const saved = writeConsentState(nextPurposes.length ? 'accepted' : 'necessary_only', nextPurposes)
    setSaveFailed(!saved)
    return !!saved
  }, [])

  const saveWithBackend = useCallback(async(nextPurposes: Array<ConsentPurpose>, method: ConsentDecision['method'], confirmation = false) => {
    if (!await saveBackendChoice(nextPurposes, method)) return
    if (confirmation) setView('confirmation')
    setIsApplying(true)
  }, [saveBackendChoice])

  const applyAndReload = useCallback((nextPurposes?: Array<ConsentPurpose>, method: ConsentDecision['method'] = 'CONFIGURE') => {
    if (isBusy) return
    if (backend.enabled && nextPurposes) return saveWithBackend(nextPurposes, method)
    if (nextPurposes && !save(nextPurposes)) return
    setIsApplying(true)
  }, [isBusy, backend.enabled, saveWithBackend, save])

  const continueWithoutSaving = useCallback(() => {
    continueWithoutOptionalCookies()
    clearUngrantedPurposeIdentifiers([])
    setIsOpen(false)
  }, [])

  const acceptAll = useCallback(() => {
    return applyAndReload([...CONSENT_PURPOSES], 'ACCEPT_ALL')
  }, [applyAndReload])

  const acceptNecessary = useCallback(() => {
    return applyAndReload([], 'NECESSARY_ONLY')
  }, [applyAndReload])

  const configure = useCallback(() => {
    if (isBusy) return
    setPurposes([])
    setOpenedFromSettings(false)
    setView('configure')
  }, [isBusy])

  const saveConfiguration = useCallback(() => {
    if (isBusy) return
    if (backend.enabled) return saveWithBackend(purposes, 'CONFIGURE', true)
    if (!save(purposes)) return
    setView('confirmation')
    setIsApplying(true)
  }, [isBusy, backend.enabled, saveWithBackend, purposes, save])

  const applySavedChoices = useCallback(() => {
    applyAndReload()
  }, [applyAndReload])

  const discardConfigure = useCallback(() => {
    if (isBusy) return
    if (openedFromSettings) {
      setIsOpen(false)
      return
    }
    setPurposes([])
    setView('first-layer')
  }, [isBusy, openedFromSettings])

  const onTogglePurpose = useCallback((purpose: ConsentPurpose, checked: boolean) => {
    setPurposes(current => {
      if (checked) {
        return current.includes(purpose) ? current : [...current, purpose]
      }
      return current.filter(item => item !== purpose)
    })
  }, [])

  const onModalClose = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    if (isBusy) return
    if (view === 'confirmation') {
      applySavedChoices()
      return
    }
    if (view === 'configure') {
      discardConfigure()
    }
  }, [applySavedChoices, discardConfigure, isBusy, view])

  const heading = view === 'first-layer'
    ? intl.formatMessage(consentMessages.heading)
    : intl.formatMessage(consentMessages.cookieSettings)
  const isFirstLayer = view === 'first-layer'
  const cookiePolicyLabel = intl.formatMessage(consentMessages.cookiePolicy)
  const applyingLabel = intl.formatMessage(consentMessages.applyingChoices)
  const canContinueWithoutSaving = (saveFailed || !!backend.error) &&
    (backend.enabled ? !currentConsent?.receipt : !currentConsent)

  if (!isOpen) return undefined

  return (
    <ModalBase
      size="S"
      isOpen
      dismissible={!isFirstLayer && !isBusy}
      onClose={onModalClose}
      data-testid="consent-modal"
    >
      <LoadingIndicator
        visible={isBusy}
        floating
        opaque
        aria-live="polite"
        aria-label={applyingLabel}
      >
        <BodyText variant="medium" align="center">{applyingLabel}</BodyText>
      </LoadingIndicator>
      <ModalHeader
        title={heading}
        dismissible={!isFirstLayer && !isBusy}
        onBackButtonClick={view === 'configure' && !isBusy ? discardConfigure : undefined}
        onCloseButtonClick={isFirstLayer || isBusy ? undefined : onModalClose}
      />
      <ModalBody>
        <ModalContent>
          {(saveFailed || backend.error) && <BodyText variant="medium" role="alert">{intl.formatMessage(getErrorMessage(backend.error, backend.enabled))}</BodyText>}
          {isFirstLayer && (
            <Group direction="vertical" gap={16}>
              <BodyText variant="large">{intl.formatMessage(consentMessages.responsibleParty)}</BodyText>
              <BodyText variant="large">{intl.formatMessage(consentMessages.optionalUse)}</BodyText>
              <BodyText variant="large">{intl.formatMessage(consentMessages.changeOrWithdraw)}</BodyText>
              <TextLink
                href={privacyURL}
                target="_blank"
                rel="noopener noreferrer"
                weight="regular"
              >
                {cookiePolicyLabel}
              </TextLink>
            </Group>
          )}
          {view === 'configure' && (
            <Group direction="vertical" gap={24}>
              <ConsentPurposeRow
                title={intl.formatMessage(consentMessages.necessaryTitle)}
                description={intl.formatMessage(consentMessages.necessaryDescription)}
                checked
                disabled
              />
              {OPTIONAL_PURPOSE_COPY.map(item => (
                <ConsentPurposeRow
                  key={item.purpose}
                  name={item.purpose}
                  title={intl.formatMessage(item.title)}
                  description={intl.formatMessage(item.description)}
                  checked={purposes.includes(item.purpose)}
                  onToggle={onTogglePurpose}
                />
              ))}
            </Group>
          )}
          {view === 'confirmation' && (
            <BodyText variant="large">{intl.formatMessage(consentMessages.savedConfirmation)}</BodyText>
          )}
        </ModalContent>
      </ModalBody>
      <ModalFooter actionsLayout="stacked" tabletActionsLayout="stacked">
        {canContinueWithoutSaving && (
          <TextButton kind="secondary" fit="full-width" size="large" onClick={continueWithoutSaving}>
            {intl.formatMessage(consentMessages.continueNecessary)}
          </TextButton>
        )}
        {isFirstLayer && (
          <>
            <TextButton kind="secondary" fit="full-width" size="large" disabled={isBusy} onClick={acceptAll}>
              {intl.formatMessage(consentMessages.acceptAll)}
            </TextButton>
            <TextButton kind="secondary" fit="full-width" size="large" disabled={isBusy} onClick={acceptNecessary}>
              {intl.formatMessage(consentMessages.necessaryOnly)}
            </TextButton>
            <TextButton kind="secondary" fit="full-width" size="large" disabled={isBusy} onClick={configure}>
              {intl.formatMessage(consentMessages.configure)}
            </TextButton>
          </>
        )}
        {view === 'configure' && (
          <TextButton kind="primary" fit="full-width" size="large" disabled={isBusy} onClick={saveConfiguration}>
            {intl.formatMessage(consentMessages.saveChoices)}
          </TextButton>
        )}
        {view === 'confirmation' && (
          <TextButton kind="primary" fit="full-width" size="large" disabled={isBusy} onClick={applySavedChoices}>
            {intl.formatMessage(consentMessages.gotIt)}
          </TextButton>
        )}
      </ModalFooter>
    </ModalBase>
  )
}

export default ConsentBanner
